import { Injectable } from '@angular/core';
import { DynamicFieldConfig } from '../interfaces';

@Injectable({
    providedIn: 'root',
})
export class MaskService {
    /**
     * Verifica se o campo é do tipo que usa máscara
     */
    isMaskedType(field: DynamicFieldConfig): string | boolean {
        const type = ['cpf', 'cnpj', 'cep', 'currency'].includes(field.type);
        if (type) {
            return field.type;
        }
        return false;
    }

    /**
     * Aplica máscaras aos dados carregados do backend
     */
    applyMasksToLoadedData(data: any, config: DynamicFieldConfig[]): any {
        if (!data || !config) return data;

        const maskedData = { ...data };

        config.forEach(field => {
            if (this.isMaskedType(field) && maskedData[field.name] !== undefined) {
                const value = maskedData[field.name];

                // Só aplica máscara se o valor não estiver já formatado
                if (typeof value === 'number' || (typeof value === 'string' && !value.includes('R$'))) {
                    maskedData[field.name] = this.formatValueForMask(field, value);
                }
            }
        });

        return maskedData;
    }

    /**
     * Formata valor para exibição com máscara
     */
    formatValueForMask(field: DynamicFieldConfig, value: any): string {
        if (!value) return '';

        switch (field.type) {
            case 'currency':
                return this.formatCurrencyForDisplay(value);
            case 'cpf':
                return this.formatCPFForDisplay(value);
            case 'cnpj':
                return this.formatCNPJForDisplay(value);
            case 'cep':
                return this.formatCEPForDisplay(value);
            default:
                return value;
        }
    }

    /**
     * Formata moeda para exibição
     */
    formatCurrencyForDisplay(value: number | string): string {
        if (!value) return '';

        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '';

        return numValue.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    /**
     * Formata CPF para exibição
     */
    formatCPFForDisplay(value: string | number): string {
        if (!value) return '';

        const strValue = value.toString().replace(/\D/g, '');
        if (strValue.length !== 11) return strValue;

        return strValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    /**
     * Formata CNPJ para exibição
     */
    formatCNPJForDisplay(value: string | number): string {
        if (!value) return '';

        const strValue = value.toString().replace(/\D/g, '');
        if (strValue.length !== 14) return strValue;

        return strValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    /**
     * Formata CEP para exibição
     */
    formatCEPForDisplay(value: string | number): string {
        if (!value) return '';

        const strValue = value.toString().replace(/\D/g, '');
        if (strValue.length !== 8) return strValue;

        return strValue.replace(/(\d{5})(\d{3})/, '$1-$2');
    }

    /**
     * Transforma campos de preço de string formatada para float
     */
    transformCurrencyFields(data: any, currencyFields: string[] = ['price', 'pricePerLiter']): any {
        const transformedData = { ...data };

        currencyFields.forEach(field => {
            if (transformedData[field] && typeof transformedData[field] === 'string') {
                // Remove R$, espaços e converte formato brasileiro para float
                const cleanValue = this.parseBrazilianCurrency(transformedData[field]);
                transformedData[field] = cleanValue;
            }
        });

        return transformedData;
    }

    /**
     * Parse valor de moeda brasileira para float
     * Ex: "R$ 1.002,66" -> 1002.66
     */
    private parseBrazilianCurrency(value: string): number {
        if (!value) return 0;

        // Remove R$, espaços e outros caracteres não numéricos exceto . e ,
        let cleanValue = value.replace(/[R$\s]/g, '');

        // Se não tem vírgula, é um número inteiro
        if (!cleanValue.includes(',')) {
            return parseFloat(cleanValue.replace(/\./g, '')) || 0;
        }

        // Se tem vírgula, é um número decimal
        // Remove pontos (separadores de milhares) e substitui vírgula por ponto
        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');

        return parseFloat(cleanValue) || 0;
    }

    /**
     * Retorna placeholder para campos com máscara
     */
    getMaskedPlaceholder(field: DynamicFieldConfig): string {
        if (field.placeholder) return field.placeholder;

        switch (field.type) {
            case 'cpf':
                return '000.000.000-00';
            case 'cnpj':
                return '00.000.000/0000-00';
            case 'cep':
                return '00000-000';
            case 'phone':
                return '(00) 00000-0000';
            case 'date':
                return '00/00/0000';
            case 'currency':
                return 'R$ 0,00';
            default:
                return '';
        }
    }
}
